import {
  DEAL_WON_STATUSES,
  HOURS_PER_DAY,
  INSTALLMENT_BILLING,
  RECURRING_BILLING,
} from '../../constants';
import type { DealLine } from '../../types/domain';
import { contractTotalMonths } from '../dates';
import { eur } from '../format';
import { toNumber } from '../numbers';
import { estimatedCommitment } from './contractLength';

const has = (v: unknown) => v != null && v !== '';
const lineName = (l: DealLine) => l.nom || 'Ligne sans nom';
const isFixedPrice = (fact: string) => fact === 'forfait_brique' || fact === 'forfait_global';

// Jours vendus d'un forfait, convertis en jours quand ils sont saisis en heures.
const soldDays = (l: DealLine) =>
  (toNumber(l.jours_vendus) || 0) / (l.unite_vendu === 'heures' ? HOURS_PER_DAY : 1);

// Forfait : le budget vendu doit être égal aux jours vendus × TJM vendu (à l'euro près, pour
// absorber l'arrondi d'un TJM déduit du budget). Renvoie le message d'écart, ou null.
export function lineFixedPriceMismatch(l: DealLine): string | null {
  if (!isFixedPrice(l.facturation || 'forfait_brique')) return null;
  if (![l.budget_vendu, l.jours_vendus, l.tjm_vendu].every(has)) return null;
  const budget = toNumber(l.budget_vendu) || 0;
  const expected = soldDays(l) * (toNumber(l.tjm_vendu) || 0);
  if (Math.abs(budget - expected) < 1) return null;
  return `${lineName(l)} — budget vendu ${eur(budget)} ≠ jours vendus × TJM vendu (${eur(expected)})`;
}

// Contrat récurrent : l'engagement (vendu, sinon estimé) doit couvrir exactement les dates de la
// ligne : 01/01/2027 + 24 mois → fin au 31/12/2028 (un mois commencé compte). Sans date de fin, rien
// à comparer : le contrat est ouvert (reconduction tacite). Un forfait n'a pas d'engagement (champ
// absent de sa fiche) : jamais contrôlé. Renvoie le message d'écart, ou null.
export function lineDurationMismatch(l: DealLine): string | null {
  if (!RECURRING_BILLING.includes(l.facturation || 'forfait_brique')) return null;
  const engagement = has(l.engagement_mois_vendu)
    ? l.engagement_mois_vendu
    : estimatedCommitment(l) || null;
  if (!has(engagement) || !l.date_debut_prevue || !l.date_fin_prevue) return null;
  const months = contractTotalMonths(l.date_debut_prevue, l.date_fin_prevue);
  if (months === toNumber(engagement)) return null;
  return `${lineName(l)} — engagement de ${engagement} mois, mais les dates couvrent ${months} mois`;
}

/* Écarts qui bloquent l'enregistrement d'une affaire, quel que soit son statut. */
export function dealLineErrors(l: DealLine): string[] {
  if (l.annulee) return [];
  return [lineDurationMismatch(l), lineFixedPriceMismatch(l)].filter((m): m is string => !!m);
}

// Contrôle "vendu" avant un statut gagné : renvoie un message si la ligne n'a pas les champs
// nécessaires à son type de facturation (ou présente un écart), ou null si elle est complète.
export function dealLineIncomplete(l: DealLine): string | null {
  if (l.annulee) return null;
  const fact = l.facturation || 'forfait_brique';
  const nom = lineName(l);
  if (isFixedPrice(fact)) {
    if (!has(l.budget_vendu)) return `${nom} — budget vendu manquant`;
    if (!has(l.jours_vendus)) return `${nom} — jours vendus manquants`;
    if (!has(l.tjm_vendu)) return `${nom} — TJM vendu manquant`;
  } else if (fact === INSTALLMENT_BILLING) {
    if (!has(l.budget_vendu)) return `${nom} — montant total vendu manquant`;
    if (!has(l.engagement_mois_vendu)) return `${nom} — nombre de mensualités manquant`;
  } else if (fact === 'regie_horaire') {
    if (!has(l.heures_vendu)) return `${nom} — heures vendues manquantes`;
    if (!has(l.taux_horaire_vendu)) return `${nom} — taux horaire vendu manquant`;
    if (!has(l.engagement_mois_vendu)) return `${nom} — engagement (mois) manquant`;
  } else if (fact === 'regie_temporelle') {
    if (!has(l.nb_devs_vendu)) return `${nom} — nombre de devs vendu manquant`;
    if (!has(l.tjm_vendu)) return `${nom} — TJM vendu manquant`;
    if (!has(l.engagement_mois_vendu)) return `${nom} — engagement (mois) manquant`;
  } else if (!has(l.montant_mensuel)) return `${nom} — montant mensuel manquant`;
  // Récurrent : sans date de début, le CA ne tomberait dans aucune année.
  if (RECURRING_BILLING.includes(fact) && !has(l.date_debut_prevue))
    return `${nom} — date de début manquante`;
  return dealLineErrors(l)[0] || null;
}

/* Ce qui bloque l'enregistrement d'une affaire (même liste pour le contrôle et le bandeau de la
   fiche) : les écarts à tout statut ; en statut gagné, aussi les champs « vendu » manquants. */
export function dealSaveProblems(lignes: DealLine[], statut: string | null | undefined): string[] {
  if (DEAL_WON_STATUSES.includes(statut as string))
    return lignes.map(dealLineIncomplete).filter((m): m is string => !!m);
  return lignes.flatMap(dealLineErrors);
}
